import React, { memo, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useAllActiveRebateSuppliers from 'src/api/accounts-payable/useAllActiveRebateSuppliersQuery';
import { SupplierDTO } from 'src/api/accounts-payable/useSuppliers';
import type { WorkflowConstants } from '../types/WorkflowConstants';
import SelectDropdown, {
  SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import { CURRENCY_CODE_VALUES } from '@erp/shared-types';

interface ConstantsReviewProps {
  constants: WorkflowConstants;
  onConstantsChange: (constants: WorkflowConstants) => void;
  disabled?: boolean;
}

// Currency options for dropdown
// NOTE: Uses shared currency codes from @erp/shared-types
const CURRENCY_OPTIONS = CURRENCY_CODE_VALUES.map((code) => ({
  code,
  label: `${code} - ${getCurrencyLabel(code)}`,
}));

function getCurrencyLabel(code: string): string {
  const labels: Record<string, string> = {
    CAD: 'Canadian Dollar',
    USD: 'US Dollar',
    EUR: 'Euro',
    GBP: 'British Pound',
  };
  return labels[code] || code;
}

const ConstantsReview: React.FC<ConstantsReviewProps> = memo(
  ({ constants, onConstantsChange, disabled = false }) => {
    // Fetch all active rebate suppliers (those with existing agreements + product groups)
    const { data: suppliers } = useAllActiveRebateSuppliers(
      undefined,
      !disabled,
    );

    // Memoized supplier options for SelectDropdown
    const supplierOptions: SelectDropdownOption[] = useMemo(() => {
      return (suppliers || []).map((s: SupplierDTO) => ({
        id: s.id,
        label: `${s.name}${s.address?.city ? ` - ${s.address.city}` : ''}`,
        display: s.name,
      }));
    }, [suppliers]);

    const handleChange = useCallback(
      (key: string, value: string) => {
        onConstantsChange({
          ...constants,
          [key]: value,
        });
      },
      [constants, onConstantsChange],
    );

    if (!constants || Object.keys(constants).length === 0) {
      return null;
    }

    return (
      <Card className="p-6 mt-6 mb-4 bg-white max-w-fit w-auto">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mt-1">
            Please review and edit these extracted details if needed before
            submitting:
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-1">
          {/* Supplier (by ID) */}
          <SelectDropdown
            options={supplierOptions}
            value={
              supplierOptions.find(
                (o) => o.id === (constants.supplierId || ''),
              ) || null
            }
            onChange={(selected) =>
              handleChange('supplierId', (selected?.id as string) || '')
            }
            label="Supplier"
            placeholder="Search supplier..."
            size="small"
            disabled={disabled}
            required
            error={!constants.supplierId}
            helperText={!constants.supplierId ? 'Required' : undefined}
          />

          {/* Currency */}
          <SelectDropdown
            options={CURRENCY_OPTIONS.map((c) => ({
              id: c.code,
              label: c.label,
              display: c.code,
            }))}
            value={
              CURRENCY_OPTIONS.map((c) => ({
                id: c.code,
                label: c.label,
                display: c.code,
              })).find((o) => o.display === constants.currency) || null
            }
            onChange={(selected) =>
              handleChange('currency', selected?.display || '')
            }
            label="Currency"
            size="small"
            disabled={disabled}
            required
            error={!constants.currency}
            helperText={!constants.currency ? 'Required' : undefined}
          />

          {/* Date Range - Always visible for manual input */}
          <div className="col-span-2 grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">
                Date Range - Start (YYYY-MM-DD)
                <span className="text-gray-500 ml-1 text-xs">(Optional)</span>
              </Label>
              <Input
                id="start-date"
                type="text"
                value={constants.dateRange?.startDate || ''}
                onChange={(e) =>
                  onConstantsChange({
                    ...constants,
                    dateRange: {
                      startDate: e.target.value,
                      endDate: constants.dateRange?.endDate || '',
                    },
                  })
                }
                disabled={disabled}
                placeholder="e.g., 2024-01-01"
                className={
                  Boolean(constants.dateRange?.startDate) &&
                  !/^\d{4}-\d{2}-\d{2}$/.test(
                    constants.dateRange?.startDate || '',
                  )
                    ? 'border-red-500'
                    : ''
                }
                pattern="^\d{4}-\d{2}-\d{2}$"
              />
              {Boolean(constants.dateRange?.startDate) &&
                !/^\d{4}-\d{2}-\d{2}$/.test(
                  constants.dateRange?.startDate || '',
                ) && <p className="text-sm text-red-500">Must be YYYY-MM-DD</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">
                Date Range - End (YYYY-MM-DD)
                <span className="text-gray-500 ml-1 text-xs">(Optional)</span>
              </Label>
              <Input
                id="end-date"
                type="text"
                value={constants.dateRange?.endDate || ''}
                onChange={(e) =>
                  onConstantsChange({
                    ...constants,
                    dateRange: {
                      startDate: constants.dateRange?.startDate || '',
                      endDate: e.target.value,
                    },
                  })
                }
                disabled={disabled}
                placeholder="e.g., 2024-12-31"
                className={
                  Boolean(constants.dateRange?.endDate) &&
                  !/^\d{4}-\d{2}-\d{2}$/.test(
                    constants.dateRange?.endDate || '',
                  )
                    ? 'border-red-500'
                    : ''
                }
                pattern="^\d{4}-\d{2}-\d{2}$"
              />
              {Boolean(constants.dateRange?.endDate) &&
                !/^\d{4}-\d{2}-\d{2}$/.test(
                  constants.dateRange?.endDate || '',
                ) && <p className="text-sm text-red-500">Must be YYYY-MM-DD</p>}
            </div>
          </div>
        </div>
      </Card>
    );
  },
);

ConstantsReview.displayName = 'ConstantsReview';

// (unused) format helper removed in favor of explicit labels

export default ConstantsReview;
